import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, TemplateRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { interval, startWith, switchMap, Subscription, throwError } from 'rxjs';
import { AppConstants } from '../app.constants';
import { AnalyticsService } from '../services/analytics.service';
import { AnalyticsEvents, AnalyticsSections } from '../analytics.constants';

@Component({
  selector: 'app-iframe-polling',
  templateUrl: './iframe-polling.component.html',
  styleUrls: ['./iframe-polling.component.scss'],
})
export class IframePollingComponent implements OnInit, OnDestroy {
  @ViewChild('iframeForm') iframeForm!: TemplateRef<any>;
  @Input() getFormUrl!: (encryptedId: string) => any;
  @Input() getConfirmationStatus!: (recordId: string) => any;
  @Input() encryptedId!: string;
  @Input() recordId!: string;

  @Output() pollingSuccess = new EventEmitter<void>();
  @Output() pollingTimeout = new EventEmitter<void>();
  @Output() pollingError = new EventEmitter<any>();
  @Output() dataCaptured = new EventEmitter<void>();

  public trustedUrl: SafeResourceUrl | undefined;
  private timeInterval: Subscription | undefined;
  public successStatus: boolean = false;
  public displayCounter: boolean = false;
  public counter: any;
  public validationError: boolean = false;
  public alertContent: string = '';
  public hasPollingTimedOut: boolean = false;

  constructor(
    private sanitizer: DomSanitizer,
    private modalService: NgbModal,
    private analyticsService: AnalyticsService
  ) {}

  ngOnInit() {}

  public openIframeModal() {
    this.validationError = false;
    this.hasPollingTimedOut = false;
    this.getFormUrl(this.encryptedId).subscribe({
      next: (res: any) => {
        this.trustedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(res.zohoURL);
        this.openModal();
        this.startPolling();
        this.alertContent = '';
        setTimeout(() => {
          this.validationError = false;
        });
      },
      error: (err: any) => {
        this.analyticsService.trackEvent(
          AnalyticsSections.BANKING_INFORMATION,
          AnalyticsEvents.BANK_DETAILS_POPUP_ERROR,
          {
            error: err,
          }
        );
        this.pollingError.emit(err);
        throwError(() => err);
      },
    });
  }

  private async openModal() {
    let ngbModalOptions: NgbModalOptions = {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      ariaLabelledBy: 'iframeForm',
      size: 'xl',
    };
    this.modalService.open(this.iframeForm, ngbModalOptions);
  }

  private startPolling() {
    this.timeInterval = interval(AppConstants.Bank_pollingInterval)
      .pipe(
        startWith(0),
        switchMap(() => this.getConfirmationStatus(this.recordId))
      )
      .subscribe({
        next: (res: any) => {
          this.successStatus = res.bankInfoUpdatedStatus;
          if (this.successStatus) {
            this.stopPolling();
            this.closeModal();
            this.validationError = false;
            this.dataCaptured.emit();
            this.pollingSuccess.emit();
            this.analyticsService.trackEvent(
              AnalyticsSections.BANKING_INFORMATION,
              AnalyticsEvents.BANK_INFORMATION_SUBMITTED
            );
          }
        },
        error: (error: any) => {
          console.log('error', error);
          this.analyticsService.trackEvent(
            AnalyticsSections.BANKING_INFORMATION,
            AnalyticsEvents.BANK_DETAILS_POLLING_ERROR,
            {
              error: error,
            }
          );
          this.pollingError.emit(error);
        },
      });

    setTimeout(() => {
      this.hasPollingTimedOut = true;
      this.stopPolling();
      this.closeModal();
      this.pollingTimeout.emit();
    }, AppConstants.Bank_StopPolling);
    this.setCounter();
    if (!this.successStatus) {
      this.validationError = true;
    }
  }

  private setCounter() {
    this.counter = AppConstants.Bank_StopPolling / 1000;
    let interval1 = setInterval(() => {
      if (this.counter < 60) {
        this.displayCounter = true;
      } else {
        this.displayCounter = false;
      }
      this.counter--;
      if (this.counter === 0) {
        this.displayCounter = false;
        clearInterval(interval1);
      }
    }, 1000);
  }

  public closeModal() {
    if (!this.successStatus && this.hasPollingTimedOut) {
      this.validationError = true;
      this.alertContent = 'Sorry! We could not capture your details. Please fill the details again';
    }
    this.modalService.dismissAll();
  }

  private stopPolling() {
    if (this.timeInterval) {
      this.timeInterval.unsubscribe();
    }
  }

  ngOnDestroy() {
    this.stopPolling();
    this.modalService.dismissAll();
  }
}
